import {
	Button,
	Checkbox,
	Input,
	Label,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { type FormEvent, useState } from "react";
import { Async, Field, PageHead, useAction, useConfirm } from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Pack, type Plan } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync } from "@/lib/hooks";

const M = 1_000_000;

export function PlansPage() {
	const t = useT();
	const plans = useAsync(() => api.plans(), []);
	const markup = useAsync(() => api.markup(), []);
	const packs = useAsync(() => api.packs(), []);

	return (
		<>
			<PageHead title={t("plans.title")} lede={t("plans.lede")} />
			<p className="hint" style={{ marginBottom: "var(--s-8)" }}>
				{t("plans.perDollar", { n: 100 })}
			</p>
			<Async state={plans}>
				{({ plans: list, orgs_on }) => (
					<div className="stack">
						{list.map((plan) => (
							<PlanForm
								key={plan.key}
								plan={plan}
								orgs={orgs_on.find((o) => o.plan_key === plan.key)?.count ?? 0}
								onSaved={plans.reload}
							/>
						))}
					</div>
				)}
			</Async>

			<div className="section">
				<h2>{t("plans.markup")}</h2>
				<p className="hint">{t("plans.markupHint")}</p>
				<Async state={markup}>
					{({ credit_markup_bp }) => <MarkupForm bp={credit_markup_bp} onSaved={markup.reload} />}
				</Async>
			</div>

			<div className="section">
				<h2>{t("plans.packs")}</h2>
				<Async state={packs}>
					{({ packs: list }) => (
						<div className="table-scroll">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t("plans.packKey")}</TableHead>
										<TableHead>{t("plans.packCredits")}</TableHead>
										<TableHead>USD</TableHead>
										<TableHead>CNY</TableHead>
										<TableHead>{t("plans.priceIds")}</TableHead>
										<TableHead>{t("plans.position")}</TableHead>
										<TableHead>{t("plans.active")}</TableHead>
										<TableHead />
									</TableRow>
								</TableHeader>
								<TableBody>
									{list.map((pack) => (
										<PackRow key={pack.key} pack={pack} onSaved={packs.reload} />
									))}
									<PackRow pack={null} onSaved={packs.reload} />
								</TableBody>
							</Table>
						</div>
					)}
				</Async>
			</div>
		</>
	);
}

function PlanForm({ plan, orgs, onSaved }: { plan: Plan; orgs: number; onSaved: () => void }) {
	const t = useT();
	const { locale } = useI18n();
	const { run, busy } = useAction();
	const [p, setP] = useState<Plan>(plan);
	const set = <K extends keyof Plan>(key: K, value: Plan[K]) => setP((prev) => ({ ...prev, [key]: value }));
	const num = (key: keyof Plan) => (e: React.ChangeEvent<HTMLInputElement>) =>
		set(key, Number(e.target.value) as never);
	const money = (key: keyof Plan) => (e: React.ChangeEvent<HTMLInputElement>) =>
		set(key, Math.round(Number(e.target.value) * M) as never);
	const text = (key: keyof Plan) => (e: React.ChangeEvent<HTMLInputElement>) => set(key, e.target.value as never);

	async function submit(e: FormEvent) {
		e.preventDefault();
		const r = await run(
			() => api.putPlan(plan.key, p),
			() => t("state.saved"),
		);
		if (r) onSaved();
	}

	return (
		<form className="card form" onSubmit={submit}>
			<div className="row">
				<h2>
					{locale === "zh" ? p.name_zh : p.name_en} <span className="mono faint">{p.key}</span>
				</h2>
				<span className="faint small">{t("plans.orgsOn", { n: orgs })}</span>
				<span className="spacer" />
				<Label className="check">
					<Checkbox checked={p.active} onCheckedChange={(checked) => set("active", checked === true)} />
					{t("plans.active")}
				</Label>
				<Label className="check">
					<Checkbox checked={p.contact_only} onCheckedChange={(checked) => set("contact_only", checked === true)} />
					{t("plans.contactOnly")}
				</Label>
			</div>
			<div className="field-row">
				<Field label={t("plans.nameEn")}>
					<Input value={p.name_en} onChange={text("name_en")} />
				</Field>
				<Field label={t("plans.nameZh")}>
					<Input value={p.name_zh} onChange={text("name_zh")} />
				</Field>
				<Field label={t("plans.position")}>
					<Input type="number" value={p.position} onChange={num("position")} />
				</Field>
			</div>
			<div className="field-row">
				<Field label={t("plans.credits")}>
					<Input type="number" min={0} value={p.monthly_credits} onChange={num("monthly_credits")} />
				</Field>
				<Field label={t("plans.entities")}>
					<Input type="number" min={0} value={p.entities_max} onChange={num("entities_max")} />
				</Field>
				<Field label={t("plans.engines")}>
					<Input type="number" min={0} value={p.engines_max} onChange={num("engines_max")} />
				</Field>
				<Field label={t("plans.seats")}>
					<Input type="number" min={0} value={p.seats_max} onChange={num("seats_max")} />
				</Field>
				<Field label={t("plans.interval")}>
					<Input type="number" min={0} value={p.sample_interval_days} onChange={num("sample_interval_days")} />
				</Field>
				<Field label={t("plans.replication")}>
					<Input type="number" min={0} max={5} value={p.replication} onChange={num("replication")} />
				</Field>
				<Field label={t("plans.questions")}>
					<Input type="number" min={0} value={p.questions_max} onChange={num("questions_max")} />
				</Field>
				<Field label={t("plans.workflows")}>
					<Input type="number" min={0} value={p.workflows_max} onChange={num("workflows_max")} />
				</Field>
				<Field label={t("plans.pagesMonth")}>
					<Input type="number" min={0} value={p.pages_per_month} onChange={num("pages_per_month")} />
				</Field>
				<Field label={t("plans.reviewFirst")}>
					<Input type="number" min={0} value={p.review_first_n} onChange={num("review_first_n")} />
				</Field>
			</div>
			<div className="row">
				<Label className="check">
					<Checkbox checked={p.cname} onCheckedChange={(checked) => set("cname", checked === true)} />
					{t("plans.cname")}
				</Label>
				<Label className="check">
					<Checkbox checked={p.trial} onCheckedChange={(checked) => set("trial", checked === true)} />
					{t("plans.trial")}
				</Label>

				<Label className="check">
					<Checkbox
						checked={p.advanced_insights}
						onCheckedChange={(checked) => set("advanced_insights", checked === true)}
					/>
					{t("plans.advanced")}
				</Label>
				<Label className="check">
					<Checkbox checked={p.review_always} onCheckedChange={(checked) => set("review_always", checked === true)} />
					{t("plans.reviewAlways")}
				</Label>
			</div>
			<div className="field-row">
				<Field label={t("plans.priceUsd")}>
					<Input
						type="number"
						min={0}
						step="0.01"
						value={p.price_usd_micros / M}
						onChange={money("price_usd_micros")}
					/>
				</Field>
				<Field label={t("plans.priceUsdYear")}>
					<Input
						type="number"
						min={0}
						step="0.01"
						value={p.price_usd_yearly_micros / M}
						onChange={money("price_usd_yearly_micros")}
					/>
				</Field>
				<Field label={t("plans.priceCny")}>
					<Input
						type="number"
						min={0}
						step="0.01"
						value={p.price_cny_micros / M}
						onChange={money("price_cny_micros")}
					/>
				</Field>
				<Field label={t("plans.priceCnyYear")}>
					<Input
						type="number"
						min={0}
						step="0.01"
						value={p.price_cny_yearly_micros / M}
						onChange={money("price_cny_yearly_micros")}
					/>
				</Field>
			</div>
			<div className="field-row">
				<Field label={`${t("plans.priceIds")} · ${t("plans.priceUsd")}`}>
					<Input value={p.price_id_usd ?? ""} onChange={(e) => set("price_id_usd", e.target.value)} />
				</Field>
				<Field label={`${t("plans.priceIds")} · ${t("plans.priceUsdYear")}`}>
					<Input value={p.price_id_usd_yearly ?? ""} onChange={(e) => set("price_id_usd_yearly", e.target.value)} />
				</Field>
				<Field label={`${t("plans.priceIds")} · ${t("plans.priceCny")}`}>
					<Input value={p.price_id_cny ?? ""} onChange={(e) => set("price_id_cny", e.target.value)} />
				</Field>
				<Field label={`${t("plans.priceIds")} · ${t("plans.priceCnyYear")}`}>
					<Input value={p.price_id_cny_yearly ?? ""} onChange={(e) => set("price_id_cny_yearly", e.target.value)} />
				</Field>
			</div>
			<div className="form-foot">
				<Button variant="default" type="submit" disabled={busy}>
					{t("state.save")}
				</Button>
				<span className="faint small">
					{fmt.usd(locale, p.price_usd_micros)} / {fmt.cny(locale, p.price_cny_micros)}
				</span>
			</div>
		</form>
	);
}

/// The one number that turns what the models charge into what a customer is
/// charged. Basis points, so 10000 is list price exactly.
function MarkupForm({ bp, onSaved }: { bp: number; onSaved: () => void }) {
	const t = useT();
	const { run, busy } = useAction();
	const confirm = useConfirm();
	const [value, setValue] = useState(String(bp));
	const parsed = Number(value);
	const dirty = parsed !== bp && parsed > 0;
	const times = (n: number) => (n / 10000).toFixed(2);
	return (
		<div className="row-actions">
			<Input
				type="number"
				min={1}
				max={100000}
				className="field-num-lg"
				value={value}
				onChange={(e) => setValue(e.target.value)}
			/>
			<span className="hint">{t("plans.markupTimes", { n: (parsed / 10000).toFixed(2) })}</span>
			<Button
				variant="default"
				type="button"
				disabled={busy || !dirty}
				onClick={async () => {
					const ok = await confirm(
						t("plans.markup"),
						t("plans.markupConfirm", { from: times(bp), to: times(parsed) }),
						true,
					);
					if (!ok) return;
					run(
						() => api.putMarkup({ credit_markup_bp: parsed }),
						() => t("state.saved"),
					).then(onSaved);
				}}
			>
				{t("state.save")}
			</Button>
		</div>
	);
}

function PackRow({ pack, onSaved }: { pack: Pack | null; onSaved: () => void }) {
	const t = useT();
	const { run, busy } = useAction();
	const [key, setKey] = useState(pack?.key ?? "");
	const [credits, setCredits] = useState(String(pack?.credits ?? 1000));
	const [usd, setUsd] = useState(String((pack?.price_usd_micros ?? 10 * M) / M));
	const [cny, setCny] = useState(String((pack?.price_cny_micros ?? 70 * M) / M));
	const [idUsd, setIdUsd] = useState(pack?.price_id_usd ?? "");
	const [idCny, setIdCny] = useState(pack?.price_id_cny ?? "");
	const [position, setPosition] = useState(String(pack?.position ?? 0));
	const [active, setActive] = useState(pack?.active ?? true);

	const body = {
		credits: Number(credits),
		price_usd_micros: Math.round(Number(usd) * M),
		price_cny_micros: Math.round(Number(cny) * M),
		price_id_usd: idUsd || null,
		price_id_cny: idCny || null,
		position: Number(position),
		active,
	};

	async function save() {
		const r = await run(
			() => (pack ? api.putPack(pack.key, body) : api.newPack({ key, ...body })),
			() => t("state.saved"),
		);
		if (r) {
			if (!pack) setKey("");
			onSaved();
		}
	}

	return (
		<TableRow>
			<TableCell>
				{pack ? (
					<span className="mono">{pack.key}</span>
				) : (
					<Input placeholder="key" value={key} onChange={(e) => setKey(e.target.value)} />
				)}
			</TableCell>
			<TableCell>
				<Input
					type="number"
					min={1}
					className="field-num"
					value={credits}
					onChange={(e) => setCredits(e.target.value)}
				/>
			</TableCell>
			<TableCell>
				<Input
					type="number"
					min={0}
					step="0.01"
					className="field-num"
					value={usd}
					onChange={(e) => setUsd(e.target.value)}
				/>
			</TableCell>
			<TableCell>
				<Input
					type="number"
					min={0}
					step="0.01"
					className="field-num"
					value={cny}
					onChange={(e) => setCny(e.target.value)}
				/>
			</TableCell>
			<TableCell>
				<div className="row">
					<Input placeholder="USD" value={idUsd} onChange={(e) => setIdUsd(e.target.value)} />
					<Input placeholder="CNY" value={idCny} onChange={(e) => setIdCny(e.target.value)} />
				</div>
			</TableCell>
			<TableCell>
				<Input type="number" className="field-num-sm" value={position} onChange={(e) => setPosition(e.target.value)} />
			</TableCell>
			<TableCell>
				<Checkbox checked={active} onCheckedChange={(checked) => setActive(checked === true)} />
			</TableCell>
			<TableCell>
				<Button variant="default" type="button" disabled={busy || (!pack && !key)} onClick={save}>
					{pack ? t("state.save") : t("plans.newPack")}
				</Button>
			</TableCell>
		</TableRow>
	);
}
